"use client";

import { Plus, ArrowRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function FeaturedBladesConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="space-y-4">
            <div className="aspect-[3/4] bg-muted/30 animate-pulse" />
            <div className="h-3 w-24 bg-muted/30 animate-pulse" />
            <div className="h-6 w-40 bg-muted/30 animate-pulse" />
            <div className="h-4 w-full bg-muted/30 animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground space-y-3">
        <p>Unable to load our current favorites.</p>
        <Button onClick={refetch} variant="outline" size="sm">
          Retry
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="font-default text-sm tracking-widest uppercase text-muted-foreground">
        No flavors available at this time.
      </p>;
  }
  return <div className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
      {products.slice(0, 4).map((product, i) => {
      const categoryLabel = product.categories && product.categories.length > 0 ? product.categories[0].name : "ARTISANAL";
      return <article key={product.id} data-index={i} className="group relative flex flex-col">
            <Link to={product.link} className="block relative overflow-hidden">
              <AspectRatio ratio={3 / 4}>
                <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/cream_texture.webp')] bg-cover bg-center opacity-60" />
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="relative h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" /> : <div className="relative h-full w-full bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/ice_cream_swirl.png')] bg-cover bg-center" />}

                {}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 opacity-0 translate-y-2 transition-all duration-500 ease-out group-hover:opacity-100 group-hover:translate-y-0 z-10">
                  <AddToCartButton productId={product.id} variant="default" size="sm" text="ADD TO CART" showQuantity={false} buttonClassName="bg-primary text-primary-foreground font-default text-[11px] tracking-[0.2em] uppercase px-5 py-2 h-auto" />
                </div>
              </AspectRatio>
            </Link>

            <div className="mt-5 flex flex-col gap-2">
              <span className="font-default text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
                {categoryLabel}
              </span>

              <Link to={product.link} className="relative inline-block w-fit">
                <h3 className="font-default italic text-foreground" style={{
              fontSize: "22px",
              lineHeight: "1.2"
            }}>
                  {product.title}
                </h3>
                <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-primary transition-all duration-500 ease-out group-hover:w-full" />
              </Link>

              <div className="mt-2 flex items-center justify-between gap-3">
                <span className="font-default text-foreground" style={{
              fontSize: "18px"
            }}>
                  ${product.price ? Number(product.price).toFixed(0) : "0"}
                </span>

                <Link to={product.link} className="inline-flex items-center gap-1.5 font-default text-[11px] tracking-[0.2em] uppercase text-muted-foreground hover:text-primary transition-colors">
                  <Plus className="h-3 w-3" strokeWidth={1.5} />
                  QUICK VIEW
                </Link>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function Favorites() {
  const wp_query_params = {
    per_page: 4,
    orderby: "date",
    order: "desc" as const
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="4188" id="current-favorites" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-16 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/cream_grain_texture.png')] bg-cover bg-center opacity-[0.03] pointer-events-none" />

      <div className="relative max-w-[1500px] mx-auto">
        {}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-20">
          <div className="flex flex-col gap-4 md:gap-6">
            <span className="font-default text-[11px] tracking-[0.25em] uppercase text-muted-foreground">
              03 — SELECTION
            </span>
            <h2 className="font-default italic text-foreground leading-[0.95]" style={{
            fontSize: "clamp(2.5rem, 6vw, 4.5rem)"
          }}>
              Our seasonal favorites.
            </h2>
          </div>

          <div className="md:pb-3">
            <Button asChild variant="link" className="text-foreground hover:text-primary font-default text-[11px] tracking-[0.25em] uppercase p-0 h-auto group/link">
              <Link to="/ShopPage" className="inline-flex items-center gap-2">
                VIEW ALL FLAVORS
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/link:translate-x-1" strokeWidth={1.5} />
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div className="h-px w-full bg-border/60 mb-16 md:mb-20" />

        {}
        <EcommerceProductsProvider wp_query={wp_query}>
          <FeaturedBladesConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}